import { useScienceTutorSection } from './scienceTutorContext';
import { Minus, Plus, ShieldAlert } from 'lucide-react';
import { useState } from 'react';

import { ChargeBalance, ShellDiagram } from './IonsBondingDiagrams';
import {
  ELEMENT_FACTS,
  IONIC_COMPOUND_CASES,
  ION_CHOICES,
  ION_COUNT_RANGE,
  ION_ELEMENT_CASES,
  ION_SPECIES,
  ION_UNSUPPORTED_CASES,
  checkIonChoice,
  checkIonCounts,
  formulaOptionFeedback,
  formulaOptions,
  ionChoiceLabel,
  ionLabel,
  ionicFormula,
  predictIon,
  shellArrangement,
  type CountCheckResult,
  type FormulaOption,
  type IonChoice,
  type IonCheckResult,
} from './ionsBonding';
import { Feedback, Panel } from './IonsBondingShared';
import { choiceClass, pillClass, primaryButtonClass } from './IonsBondingStyles';

const PICKER_CASES = [
  ...ION_ELEMENT_CASES.map((entry) => ({ id: entry.id, symbol: entry.symbol, unsupported: false })),
  ...ION_UNSUPPORTED_CASES.map((entry) => ({ id: entry.id, symbol: entry.symbol, unsupported: true })),
];

const TRANSITION_IONS = ION_SPECIES.filter((ion) => ion.basis === 'reviewed-data' && !ion.polyatomic);

function IonFromShells() {
  const [caseId, setCaseId] = useState(PICKER_CASES[0].id);
  const [choice, setChoice] = useState<IonChoice | null>(null);
  useScienceTutorSection('IonFromShells', { caseId, choice });
  const [result, setResult] = useState<IonCheckResult | null>(null);
  const picked = PICKER_CASES.find((entry) => entry.id === caseId) ?? PICKER_CASES[0];
  const fact = ELEMENT_FACTS[picked.symbol];
  const prediction = predictIon(picked.symbol);
  const shells = shellArrangement(fact.atomicNumber);

  const select = (id: string) => {
    setCaseId(id);
    setChoice(null);
    setResult(null);
  };

  return (
    <Panel
      eyebrow="Week 3 · electron arrangement"
      title="From shells to ions"
      description="Pick an element, read its shells, then predict the ion it forms. Atoms lose or gain electrons to reach a full outer shell."
    >
      <div className="flex flex-wrap gap-2" role="group" aria-label="Choose an element">
        {PICKER_CASES.map((entry) => (
          <button
            key={entry.id}
            type="button"
            aria-pressed={entry.id === caseId}
            onClick={() => select(entry.id)}
            className={pillClass(entry.id === caseId)}
          >
            {ELEMENT_FACTS[entry.symbol].name}
            {entry.unsupported ? <span className="sr-only"> (outside the shell rule)</span> : null}
          </button>
        ))}
      </div>

      {prediction.kind === 'unsupported' ? (
        <div role="region" aria-label="Shell rule boundary" className="mt-6 rounded-2xl border border-brand-sunshine/50 bg-wash-sunshine p-5">
          <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
            <ShieldAlert className="h-4 w-4 shrink-0 text-brand-coral" aria-hidden="true" />
            {fact.name}: the simple shell rule stops here
          </p>
          <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">{prediction.reason}</p>
          <p className="mt-4 text-xs font-extrabold uppercase tracking-wide text-slate2">
            Reviewed transition-metal ions (learn these; the shell rule does not predict them)
          </p>
          <ul className="mt-2 flex flex-wrap gap-2">
            {TRANSITION_IONS.map((ion) => (
              <li key={ion.id} className="rounded-full bg-white px-3 py-1.5 text-sm font-extrabold text-ink">
                {ionLabel(ion)} <span className="font-medium text-slate2">{ion.name}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <div className="grid grid-cols-2 gap-3">
            {shells ? <ShellDiagram symbol={picked.symbol} shells={shells} label={`${fact.name} atom`} /> : null}
            {result && prediction.kind === 'ion' ? (
              <ShellDiagram
                symbol={picked.symbol}
                shells={prediction.shellsAfter}
                charge={prediction.charge}
                label={`${fact.name} ion`}
              />
            ) : (
              <div className="flex items-center justify-center rounded-2xl border border-dashed border-hairline bg-surface p-3 text-center text-xs font-bold leading-5 text-slate2">
                Predict first — the ion appears after you check.
              </div>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-extrabold text-ink">
              {fact.name} (Z = {fact.atomicNumber}). Which ion does it form?
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4" role="group" aria-label="Predict the ion">
              {ION_CHOICES.map((option) => (
                <button
                  key={String(option)}
                  type="button"
                  aria-pressed={choice === option}
                  onClick={() => {
                    setChoice(option);
                    setResult(null);
                  }}
                  className={choiceClass(choice === option)}
                >
                  {ionChoiceLabel(option)}
                </button>
              ))}
            </div>
            <button
              type="button"
              disabled={choice === null}
              onClick={() => choice !== null && setResult(checkIonChoice(picked.symbol, choice))}
              className={`${primaryButtonClass} mt-4`}
            >
              Check ion
            </button>
            {result ? (
              <Feedback correct={result.correct} label="Ion feedback">
                <p>{result.feedback}</p>
              </Feedback>
            ) : null}
          </div>
        </div>
      )}
    </Panel>
  );
}

function FormulaBuilder() {
  const [compoundId, setCompoundId] = useState(IONIC_COMPOUND_CASES[0].id);
  const [cationCount, setCationCount] = useState(1);
  const [anionCount, setAnionCount] = useState(1);
  const [countResult, setCountResult] = useState<CountCheckResult | null>(null);
  const [writtenChoice, setWrittenChoice] = useState<FormulaOption | null>(null);
  useScienceTutorSection('FormulaBuilder', { compoundId, cationCount, anionCount, writtenChoice });
  const compound = IONIC_COMPOUND_CASES.find((entry) => entry.id === compoundId) ?? IONIC_COMPOUND_CASES[0];
  const model = ionicFormula(compound.cationId, compound.anionId);
  if (!model) return null;
  const options = formulaOptions(compound);
  const positive = cationCount * model.cation.charge;
  const negative = anionCount * Math.abs(model.anion.charge);
  const cationText = ionLabel(model.cation);
  const anionText = ionLabel(model.anion);

  const resetCounts = (id: string) => {
    setCompoundId(id);
    setCationCount(1);
    setAnionCount(1);
    setCountResult(null);
    setWrittenChoice(null);
  };

  const adjust = (which: 'cation' | 'anion', delta: number) => {
    const setter = which === 'cation' ? setCationCount : setAnionCount;
    setter((value) => Math.min(ION_COUNT_RANGE.max, Math.max(ION_COUNT_RANGE.min, value + delta)));
    setCountResult(null);
    setWrittenChoice(null);
  };

  const steppers = [
    { key: 'cation' as const, text: cationText, name: model.cation.name, count: cationCount },
    { key: 'anion' as const, text: anionText, name: model.anion.name, count: anionCount },
  ];

  return (
    <Panel
      eyebrow="Week 3 · ionic formulas"
      title="Balance the charges"
      description="Choose a compound, add ions until the total positive and negative charges cancel, then write the formula."
    >
      <label className="block text-sm font-extrabold text-ink">
        Compound
        <select
          value={compound.id}
          onChange={(event) => resetCounts(event.target.value)}
          className="mt-2 block w-full rounded-2xl border border-hairline bg-white px-4 py-3 text-sm font-bold text-ink sm:max-w-sm"
        >
          {IONIC_COMPOUND_CASES.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </select>
      </label>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div className="grid gap-3">
          {steppers.map((stepper) => (
            <div key={stepper.key} className="flex items-center justify-between gap-3 rounded-2xl border border-hairline bg-white px-4 py-3">
              <span className="min-w-0">
                <span className="block text-lg font-extrabold text-ink">{stepper.text}</span>
                <span className="block text-xs font-bold text-slate2">{stepper.name} ion</span>
              </span>
              <span className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label={`Fewer ${stepper.text}`}
                  onClick={() => adjust(stepper.key, -1)}
                  disabled={stepper.count <= ION_COUNT_RANGE.min}
                  className="rounded-full border border-hairline p-2 text-ink disabled:opacity-40"
                >
                  <Minus className="h-4 w-4" aria-hidden="true" />
                </button>
                <output aria-label={`Number of ${stepper.text}`} className="w-6 text-center text-lg font-extrabold text-ink">
                  {stepper.count}
                </output>
                <button
                  type="button"
                  aria-label={`More ${stepper.text}`}
                  onClick={() => adjust(stepper.key, 1)}
                  disabled={stepper.count >= ION_COUNT_RANGE.max}
                  className="rounded-full border border-hairline p-2 text-ink disabled:opacity-40"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                </button>
              </span>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              setCountResult(checkIonCounts(compound, cationCount, anionCount));
              setWrittenChoice(null);
            }}
            className={`${primaryButtonClass} justify-self-start`}
          >
            Check ratio
          </button>
        </div>
        <ChargeBalance positive={positive} negative={negative} cationLabel={cationText} anionLabel={anionText} />
      </div>

      {countResult ? (
        <Feedback correct={countResult.status === 'correct'} label="Ratio feedback">
          <p>{countResult.feedback}</p>
        </Feedback>
      ) : null}

      {countResult?.status === 'correct' ? (
        <div className="mt-5">
          <p className="text-sm font-extrabold text-ink">Now write it. Which formula is written correctly?</p>
          <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Choose the written formula">
            {options.map((option) => (
              <button
                key={option.formula}
                type="button"
                aria-pressed={writtenChoice?.formula === option.formula}
                onClick={() => setWrittenChoice(option)}
                className={pillClass(writtenChoice?.formula === option.formula)}
              >
                {option.formula}
              </button>
            ))}
          </div>
          {writtenChoice ? (
            <Feedback correct={writtenChoice.kind === 'correct'} label="Formula feedback">
              <p>
                {formulaOptionFeedback(writtenChoice.kind)}{' '}
                {writtenChoice.kind === 'correct' ? `${compound.name} is ${compound.formula}.` : ''}
              </p>
            </Feedback>
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}

export function IonsMode() {
  return (
    <div className="space-y-6">
      <IonFromShells />
      <FormulaBuilder />
    </div>
  );
}
